import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { UploadCloud, FileSpreadsheet, X, ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function AdminUpload() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [files, setFiles] = useState([]);
  const [category, setCategory] = useState("hr");
  const [drag, setDrag] = useState(false);
  const [loading, setLoading] = useState(false);

  const add = (list) => {
    const arr = Array.from(list).filter(f => /\.(csv|xlsx|xls)$/i.test(f.name));
    if (!arr.length) return toast.error("Only CSV/XLSX/XLS");
    setFiles(prev => {
      const seen = new Set(prev.map(f => f.name + f.size));
      const merged = [...prev];
      for (const f of arr) { if (!seen.has(f.name + f.size) && merged.length < 10) merged.push(f); }
      return merged;
    });
  };
  const rm = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const loadSample = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/datasets/load-sample");
      toast.success(`${data.created.length} sample datasets loaded`);
      nav(`/t/${slug}/datasets`);
    } catch { toast.error("Failed"); }
    finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!files.length) return toast.error("Add at least one file");
    setLoading(true);
    const fd = new FormData();
    files.forEach(f => fd.append("files", f));
    fd.append("category", category);
    try {
      const { data } = await api.post("/datasets/upload-multi", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const c = data.created?.length || 0;
      if (c) toast.success(`${c} dataset${c > 1 ? "s" : ""} uploaded`);
      if (data.errors?.length) toast.error(`${data.errors.length} failed`);
      if (c === 1) nav(`/t/${slug}/datasets/${data.created[0].id}`);
      else if (c > 1) nav(`/t/${slug}/datasets`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 lg:p-10">
      <Link to={`/t/${slug}/datasets`} className="link-quiet inline-flex items-center gap-2 text-sm mb-6">
        <ArrowLeft size={14} /> Back
      </Link>
      <div className="label-caps mb-2">Admin · Ingest</div>
      <h1 className="font-display text-4xl font-medium tracking-tight text-slate-900">Upload HR data</h1>
      <p className="text-slate-500 mt-3 mb-8">Drop files from any HR system — Workday, SAP, BambooHR, or plain Excel. Up to 10 files at once, 20MB each.</p>

      <div className="card mb-6 flex items-center gap-4">
        <div className="w-10 h-10 rounded-lg bg-[var(--primary-soft)] flex items-center justify-center">
          <Sparkles size={16} className="text-[var(--primary)]" />
        </div>
        <div className="flex-1">
          <div className="font-medium text-slate-900">Want to try before uploading?</div>
          <div className="text-sm text-slate-500">Load 3 realistic HR sample datasets in one click.</div>
        </div>
        <button onClick={loadSample} disabled={loading} className="btn-soft" data-testid="load-sample-btn">Load sample data</button>
      </div>

      <form onSubmit={submit} className="space-y-6" data-testid="upload-form">
        <label
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); add(e.dataTransfer.files); }}
          className={`block border-2 border-dashed rounded-xl cursor-pointer p-10 text-center transition-colors ${drag ? "border-[var(--primary)] bg-[var(--primary-soft)]" : "border-[var(--border-strong)] hover:border-[var(--primary)] bg-white"}`}
          data-testid="dropzone"
        >
          <input data-testid="file-input" type="file" multiple accept=".csv,.xlsx,.xls" className="hidden"
            onChange={(e) => add(e.target.files || [])} />
          <UploadCloud size={32} className="mx-auto text-slate-400 mb-3" />
          <div className="font-medium text-slate-900">Click or drop files here</div>
          <div className="text-sm text-slate-500 mt-1">Multiple CSV · XLSX · XLS from any HR system</div>
        </label>

        {files.length > 0 && (
          <div className="space-y-2" data-testid="file-list">
            <div className="label-caps">Queued ({files.length}/10)</div>
            {files.map((f, i) => (
              <div key={i} className="flex items-center justify-between bg-white border border-[var(--border)] rounded-lg px-4 py-2.5">
                <div className="flex items-center gap-3 min-w-0">
                  <FileSpreadsheet size={16} className="text-[var(--primary)] flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="mono text-sm text-slate-900 truncate">{f.name}</div>
                    <div className="text-xs text-slate-500">{(f.size / 1024).toFixed(1)} KB</div>
                  </div>
                </div>
                <button type="button" onClick={() => rm(i)} data-testid={`remove-file-${i}`} className="text-slate-400 hover:text-slate-900"><X size={16} /></button>
              </div>
            ))}
          </div>
        )}

        <div>
          <label className="label-caps block mb-2">Category</label>
          <select data-testid="ds-category" value={category} onChange={(e) => setCategory(e.target.value)} className="input-base">
            <option value="hr">HR / People</option>
            <option value="payroll">Payroll</option>
            <option value="performance">Performance</option>
            <option value="engagement">Engagement</option>
            <option value="recruiting">Recruiting</option>
            <option value="general">General</option>
          </select>
        </div>

        <button type="submit" disabled={loading || !files.length} data-testid="upload-submit" className="btn-primary w-full justify-center">
          {loading ? "Uploading…" : `Upload ${files.length || ""} file${files.length === 1 ? "" : "s"}`}
        </button>
      </form>
    </div>
  );
}
